import { useEffect, useMemo, useState } from "react";
import { Money } from "@shop-ledger/shared-types";
import { DataTable, Button, Modal, usePagination, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { listSalesExt } from "../../api/salesApi";
import { getShipmentStatusHistory, listShipments, updateShipment } from "../../api/shipmentsApi";
import { getInventoryItem } from "../../api/inventoryApi";
import { listProductImages } from "../../api/imagesApi";
import type { SaleItemOut, SaleOut, ShipmentOut, ShipmentStatus, ShipmentStatusEventOut } from "../../api/types";
import { API_ROOT_URL } from "../../env";
import { SHIPMENT_STATUS_BADGE_CLASS, SHIPMENT_NEXT_STATUSES } from "../../domain/shipmentStatus";
import { StatusBanner } from "../../components/StatusBanner";
import { ApiError } from "../../lib/apiClient";
import { canWriteShipments } from "../../roles/permissions";
import { useAsyncData } from "../../lib/useAsyncData";

const TENANT_CURRENCY_FALLBACK = "UGX"; // Tenant.currency isn't returned by GET /auth/me
// (UserOut, `@shop-ledger/shared-types`'s api/auth.ts) — there is no tenant-settings
// endpoint this app can read it from yet, same documented stand-in `TillPage.tsx` uses.

/** Full postal address in one string, same formatting `ShipmentsPage` uses. */
function formatAddress(shipment: ShipmentOut): string {
  const lines = [shipment.address_line1, shipment.address_line2].filter(Boolean);
  const locality = [shipment.city, shipment.region, shipment.postal_code].filter(Boolean).join(", ");
  return [...lines, locality, shipment.country].filter(Boolean).join(", ");
}

interface OnlineOrderRow {
  sale: SaleOut;
  shipment: ShipmentOut | undefined;
}

interface ItemDisplayInfo {
  name: string;
  thumbUrl: string | null;
}

/**
 * §10's ECOM_MGR "online order queue" — `Sale.source === "ECOMMERCE"` rows from
 * `GET /sales`, filtered client-side (the backend route has no `source` query param, only
 * `branch_id` — `backend/app/api/v1/sales.py::list_sales`). No date/timestamp field exists
 * on `SaleOut` either, so this is an unordered snapshot, not a chronologically sorted
 * queue — flagged here rather than implying an ordering this data can't actually support.
 *
 * Joined against `GET /shipments` by `sale_id`: every `ECOMMERCE` order gets a `Shipment`
 * auto-created at checkout time (`backend/app/api/v1/public/checkout.py::
 * _place_shipment_for_order`), which is the only place a guest or signed-in customer's
 * name/email/phone/address is captured — `SaleOut` itself has no such columns. Opening a
 * row loads that shipment's line items (with product thumbnails) and its full status
 * history, and lets a shipment's status be advanced right from the same detail view.
 */
export function OnlineOrdersPage() {
  const { user, ensureFreshToken } = useAuth();
  const canWrite = user ? canWriteShipments(user.role) : false;

  const salesState = useAsyncData(() => ensureFreshToken().then((token) => listSalesExt(token)), []);
  const shipmentsState = useAsyncData(() => ensureFreshToken().then((token) => listShipments(token)), []);

  const [statusError, setStatusError] = useState<string | null>(null);
  const [selected, setSelected] = useState<OnlineOrderRow | null>(null);
  const [statusDraft, setStatusDraft] = useState<ShipmentStatus | "">("");
  const [updating, setUpdating] = useState(false);

  const [history, setHistory] = useState<ShipmentStatusEventOut[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Keyed by inventory_item_id — populated lazily as orders are opened, so the same
  // product across multiple orders viewed in one session is only ever fetched once.
  const [itemInfo, setItemInfo] = useState<Record<string, ItemDisplayInfo>>({});
  const [itemInfoError, setItemInfoError] = useState<string | null>(null);

  const rows = useMemo<OnlineOrderRow[]>(() => {
    const shipmentsBySaleId = new Map((shipmentsState.data ?? []).filter((s) => s.sale_id).map((s) => [s.sale_id as string, s]));
    return (salesState.data ?? [])
      .filter((sale) => sale.source === "ECOMMERCE")
      .map((sale) => ({ sale, shipment: shipmentsBySaleId.get(sale.id) }));
  }, [salesState.data, shipmentsState.data]);
  const { pageData: ordersPageData, pagination: ordersPagination, setPage: setOrdersPage } = usePagination(rows, 25);

  function openOrder(row: OnlineOrderRow) {
    setSelected(row);
    setStatusDraft("");
    setStatusError(null);
  }

  // Load the selected order's shipment status history + any not-yet-seen line items'
  // name/thumbnail, every time a different order is opened.
  useEffect(() => {
    if (!selected) return;
    let cancelled = false;

    setHistory(null);
    setHistoryError(null);
    if (selected.shipment) {
      setHistoryLoading(true);
      ensureFreshToken()
        .then((token) => getShipmentStatusHistory(token, selected.shipment!.id))
        .then((events) => {
          if (!cancelled) setHistory(events);
        })
        .catch((error: unknown) => {
          if (!cancelled) setHistoryError(error instanceof ApiError ? error.message : "Could not load status history.");
        })
        .finally(() => {
          if (!cancelled) setHistoryLoading(false);
        });
    }

    const unseenIds = [...new Set(selected.sale.items.map((line) => line.inventory_item_id))].filter((id) => !(id in itemInfo));
    if (unseenIds.length > 0) {
      setItemInfoError(null);
      ensureFreshToken()
        .then((token) =>
          Promise.all(
            unseenIds.map(async (itemId) => {
              const [item, images] = await Promise.all([
                getInventoryItem(token, itemId).catch(() => null),
                listProductImages(token, itemId).catch(() => []),
              ]);
              const primary = images.find((img) => img.is_primary) ?? images[0];
              const info: ItemDisplayInfo = {
                name: item?.name ?? "Unknown product",
                thumbUrl: primary ? `${API_ROOT_URL}${primary.thumb_path}` : null,
              };
              return [itemId, info] as const;
            }),
          ),
        )
        .then((entries) => {
          if (cancelled) return;
          setItemInfo((prev) => {
            const next = { ...prev };
            for (const [id, info] of entries) next[id] = info;
            return next;
          });
        })
        .catch(() => {
          if (!cancelled) setItemInfoError("Could not load some product details.");
        });
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  async function handleStatusUpdate() {
    if (!selected?.shipment || !statusDraft) return;
    const shipment = selected.shipment;
    setStatusError(null);
    setUpdating(true);
    try {
      const token = await ensureFreshToken();
      const updatedShipment = await updateShipment(token, shipment.id, { status: statusDraft });
      setStatusDraft("");
      setSelected((prev) => (prev ? { ...prev, shipment: updatedShipment } : prev));
      shipmentsState.reload();
      const events = await getShipmentStatusHistory(token, shipment.id);
      setHistory(events);
    } catch (error) {
      setStatusError(error instanceof ApiError ? error.message : "Could not update shipment status — check the transition is valid.");
    } finally {
      setUpdating(false);
    }
  }

  const columns: DataTableColumn<OnlineOrderRow, "order" | "customer" | "items" | "total" | "payment" | "shipment" | "branch">[] = [
    { key: "order", header: "Order", render: (r) => <span className="muted">{r.sale.id.slice(0, 8)}</span> },
    { key: "customer", header: "Customer", render: (r) => r.shipment?.recipient_name ?? <span className="muted">No shipment on file</span> },
    { key: "items", header: "Items", align: "right", render: (r) => r.sale.items.length },
    { key: "total", header: "Total", align: "right", render: (r) => Money.format(r.sale.grand_total, TENANT_CURRENCY_FALLBACK) },
    { key: "payment", header: "Payment", render: (r) => r.sale.payment_status },
    {
      key: "shipment",
      header: "Shipment status",
      render: (r) =>
        r.shipment ? (
          <span className={`badge ${SHIPMENT_STATUS_BADGE_CLASS[r.shipment.status] ?? "badge--current"}`}>{r.shipment.status}</span>
        ) : (
          <span className="muted">No shipment</span>
        ),
    },
    { key: "branch", header: "Branch", render: (r) => <span className="muted">{r.sale.branch_id.slice(0, 8)}</span> },
  ];

  const loading = salesState.loading || shipmentsState.loading;
  const nextStatusOptions = selected?.shipment ? SHIPMENT_NEXT_STATUSES[selected.shipment.status] ?? [] : [];

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Online Orders</h1>
          <p className="page__subtitle">
            Orders placed through your online store, with the delivery contact captured at checkout. Click an order for its
            items, shipment status, and full status history.
          </p>
        </div>
      </div>

      {salesState.error ? <StatusBanner kind="error">{salesState.error}</StatusBanner> : null}
      {shipmentsState.error ? <StatusBanner kind="error">{shipmentsState.error}</StatusBanner> : null}
      {!canWrite && user ? (
        <StatusBanner kind="info">Your role ({user.role}) can view orders. Advancing shipment status is restricted to OWNER/BRANCH_MANAGER/ECOM_MGR.</StatusBanner>
      ) : null}

      <div className="panel">
        {loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable
            columns={columns}
            rows={ordersPageData}
            getRowId={(row) => row.sale.id}
            onRowClick={openOrder}
            pagination={ordersPagination}
            onPageChange={setOrdersPage}
            emptyMessage="No online orders yet."
            caption="Online Orders"
          />
        )}
      </div>

      <Modal open={selected !== null} onClose={() => setSelected(null)} title={selected ? `Order ${selected.sale.id.slice(0, 8)}` : undefined}>
        {selected ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div>
              <h3 style={{ marginBottom: 4 }}>Customer</h3>
              {selected.shipment ? (
                <div>
                  <div>{selected.shipment.recipient_name}</div>
                  <div className="muted">{selected.shipment.phone ?? "—"}</div>
                  {selected.shipment.contact_email ? <div className="muted">{selected.shipment.contact_email}</div> : null}
                  <div className="muted">{formatAddress(selected.shipment)}</div>
                </div>
              ) : (
                <p className="muted">No shipment on file for this order.</p>
              )}
            </div>

            <div>
              <h3 style={{ marginBottom: 4 }}>Items</h3>
              {itemInfoError ? <StatusBanner kind="error">{itemInfoError}</StatusBanner> : null}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {selected.sale.items.map((line: SaleItemOut) => {
                  const info = itemInfo[line.inventory_item_id];
                  return (
                    <div key={line.id} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: 6,
                          border: "1px solid #ddd",
                          overflow: "hidden",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "#f4f4f1",
                        }}
                      >
                        {info?.thumbUrl ? (
                          <img src={info.thumbUrl} alt={info.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        ) : (
                          <span className="muted" style={{ fontSize: "0.65rem" }}>
                            No image
                          </span>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div>{info?.name ?? "Loading…"}</div>
                        <div className="muted">
                          {line.quantity} × {Money.format(line.unit_price, TENANT_CURRENCY_FALLBACK)}
                        </div>
                      </div>
                      <div style={{ fontWeight: 600 }}>{Money.format(line.total_price, TENANT_CURRENCY_FALLBACK)}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 12, textAlign: "right", fontWeight: 700 }}>
                Total: {Money.format(selected.sale.grand_total, TENANT_CURRENCY_FALLBACK)}
              </div>
            </div>

            {selected.shipment ? (
              <div>
                <h3 style={{ marginBottom: 4 }}>Shipment status</h3>
                {statusError ? <StatusBanner kind="error">{statusError}</StatusBanner> : null}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <span className={`badge ${SHIPMENT_STATUS_BADGE_CLASS[selected.shipment.status] ?? "badge--current"}`}>
                    {selected.shipment.status}
                  </span>
                  {canWrite && nextStatusOptions.length > 0 ? (
                    <>
                      <select
                        className="sl-input"
                        style={{ width: 150 }}
                        value={statusDraft}
                        onChange={(e) => setStatusDraft(e.target.value as ShipmentStatus)}
                      >
                        <option value="">Advance to…</option>
                        {nextStatusOptions.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                      <Button type="button" variant="secondary" size="md" disabled={!statusDraft || updating} onClick={() => void handleStatusUpdate()}>
                        {updating ? "Updating…" : "Update"}
                      </Button>
                    </>
                  ) : null}
                </div>

                <h4 style={{ margin: "12px 0 4px", fontSize: "0.9rem" }}>Status history</h4>
                {historyError ? <StatusBanner kind="error">{historyError}</StatusBanner> : null}
                {historyLoading ? (
                  <p className="muted">Loading…</p>
                ) : history && history.length > 0 ? (
                  <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 4 }}>
                    {history.map((event) => (
                      <li key={event.id} style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                        <span className={`badge ${SHIPMENT_STATUS_BADGE_CLASS[event.status] ?? "badge--current"}`}>{event.status}</span>
                        <span className="muted">{new Date(event.created_at).toLocaleString()}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="muted">No history recorded.</p>
                )}
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
